import { CircularProgress, Dialog, LinearProgress } from "@mui/material";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import AlertDialog from "../../components/shared/AlertDialog";
import { FileDropzone } from "../../components/shared/FileDropzone";
import {
  Box,
  ButtonCancelSecondary,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import {
  useDownloadTemplate,
  useImportExcel,
  useValidateExcel,
} from "../../queries";
import { ImportRes, ValidateRes } from "../../types/Grant";
import { downloadS3File } from "../../utils/DownloadFile";
import convertToBase64 from "../../utils/convertToBase64";
import { Instructions } from "../../components/shared/Instructions";
import { getSocketConnection } from "../../utils/socketConnection";
import OnboardingReportDialog from "./OnboardingReportDialog";

const onboardingInstructions = [
  "The file has four sheets — Employees, Grants, Vestings, and Exercises",
  "Employees sheet: Employee ID, Employee Name, Employment Status (Employed / Resigned), Email, Department, Nationality, Country Residing, Date of Join, and Employment Type are required columns",
  "Employees sheet: use Employer Name or Subsidiary Company to tag an employee to a subsidiary. The value must match one of this company's active subsidiaries",
  "Grants sheet: Employee ID, Plan Name, Options Granted, Grant Price, Date of Grant, and Grant ID are required; Vesting Template and Grant State are optional",
  "Vestings sheet: Grant Identifier, Vesting Date, and Projected Vested Options are required per row",
  "Exercises sheet: Grant Identifier, Exercise Date, and Options Exercised are required per row",
  "All dates must be in DD/MM/YYYY format",
  "Vesting Template names in the Grant sheet will be used to create templates tagged as (Historic); leave blank to use a default historic template",
  "Employee IDs and Grant IDs must be unique and must not already exist in the system",
  "For resigned employees, Last Day is required in the Employees sheet",
];

const defaultErrorColDef: ColDef = {
  flex: 1,
  sortable: true,
  resizable: true,
  wrapText: true,
  autoHeight: true,
};

const basicErrorColDefs: ColDef[] = [
  { field: "id", headerName: "ID", flex: 1 },
  { field: "name", headerName: "NAME", flex: 1 },
  { field: "errors", headerName: "ISSUE AT", flex: 2 },
];

const employeeErrorColDefs: ColDef[] = [
  { field: "id", headerName: "EMPLOYEE ID", flex: 1 },
  { field: "name", headerName: "EMPLOYEE NAME", flex: 1 },
  { field: "errors", headerName: "ISSUE AT", flex: 2 },
];

const grantSheetErrorColDefs: ColDef[] = [
  { field: "id", headerName: "GRANT ID", flex: 1 },
  { field: "errors", headerName: "ISSUE AT", flex: 3 },
];

const grantErrorColDefs: ColDef[] = [
  { field: "id", headerName: "GRANT ID", flex: 1 },
  { field: "name", headerName: "EMPLOYEE NAME", flex: 1 },
  { field: "errors", headerName: "ISSUE AT", flex: 2 },
];

interface ImportPhaseData {
  phase: string;
  step: number;
  totalSteps: number;
  phaseTotal: number;
  message: string;
  isComplete: boolean;
}

function GrantDetails() {
  const initalValidateResponse = {
    basicValidationErrors: [],
    employeeErrors: [],
    exerciseErrors: [],
    grantErrors: [],
    vestingErrors: [],
  };
  const [response, setResponse] = useState<ValidateRes>(initalValidateResponse);
  const [base64File, setBase64File] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const { mutate: validateExcel, isLoading: isValidating } = useValidateExcel();
  const { refetch: refetchForDownloadTemplate } = useDownloadTemplate();
  const { mutate: importAll, isLoading: isImporting } = useImportExcel();
  const [progress, setProgress] = useState(0);
  const [dialog, setDialog] = useState<{
    open: boolean;
    message?: string;
  }>({
    open: false,
  });
  const [isUploaded, setIsUploaded] = useState(false);
  const [summaryDetails, setSummaryDetails] = useState<ImportRes[]>([]);
  const [expandedEmployee, setExpandedEmployee] = useState(true);
  const [expandedBasic, setExpandedBasic] = useState(true);
  const [graphTextBasic, setGraphTextBasic] = useState("-");
  const [graphTextEmployee, setGraphTextEmployee] = useState("-");
  const [graphTextGrant, setGraphTextGrant] = useState("-");
  const [graphTextExercise, setGraphTextExercise] = useState("-");
  const [graphTextVesting, setGraphTextVesting] = useState("-");
  const [expandedGrant, setExpandedGrant] = useState(true);
  const [expandedExercise, setExpandedExercise] = useState(true);
  const [expandedVesting, setExpandedVesting] = useState(true);
  const [reportDialog, setReportDialog] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const [importPhaseData, setImportPhaseData] =
    useState<ImportPhaseData | null>(null);

  useEffect(() => {
    const socket = getSocketConnection();
    if (isImporting) {
      socket.on("onboarding-import-metrics", (data: ImportPhaseData) => {
        setImportPhaseData(data);
      });
    }
    return () => {
      socket.off("onboarding-import-metrics");
    };
  }, [isImporting]);
  const [searchBasic, setSearchBasic] = useState("");
  const [searchEmployee, setSearchEmployee] = useState("");
  const [searchGrant, setSearchGrant] = useState("");
  const [searchExercise, setSearchExercise] = useState("");
  const [searchVesting, setSearchVesting] = useState("");

  async function handleFileSelect(file?: File) {
    if (!file) return;
    const base64 = (await convertToBase64(file)) as string;
    setSelectedFile(file);
    setBase64File(base64);
  }

  function clearFile() {
    setSelectedFile(undefined);
    setBase64File("");
    setResponse(initalValidateResponse);
    setProgress(0);
    setImportPhaseData(null);
  }
  const employeeErrors = useMemo(
    () => response.employeeErrors ?? [],
    [response.employeeErrors]
  );
  const grantErrors = useMemo(
    () => response.grantErrors ?? [],
    [response.grantErrors]
  );
  const exerciseErrors = useMemo(
    () => response.exerciseErrors ?? [],
    [response.exerciseErrors]
  );
  const vestingErrors = useMemo(
    () => response.vestingErrors ?? [],
    [response.vestingErrors]
  );
  const basicValidationErrors = useMemo(
    () => response.basicValidationErrors ?? [],
    [response.basicValidationErrors]
  );
  const errors =
    employeeErrors.length > 0 ||
    grantErrors.length > 0 ||
    exerciseErrors.length > 0 ||
    vestingErrors.length > 0 ||
    basicValidationErrors.length > 0;
  const navigate = useNavigate();
  function clearForm(e: React.MouseEvent<HTMLInputElement, MouseEvent>) {
    e.currentTarget.value = "";
    setBase64File("");
    setResponse(initalValidateResponse);
    setProgress(0);
  }
  const handleValidate = () => {
    validateExcel(base64File ?? "", {
      onSuccess: (data) => {
        setResponse(data.data);
        setProgress(100);
      },
      onError: (err: any) => {
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  };

  async function downloadTemplate() {
    const template = await refetchForDownloadTemplate();
    downloadS3File(template.data?.data.s3link ?? "");
  }

  async function handleImport() {
    setImportPhaseData(null);
    importAll(base64File ?? "", {
      onSuccess: (data) => {
        setIsUploaded(true);
        setSummaryDetails(data.data);
        toast("Historical data uploaded successfully", {
          type: "success",
          autoClose: 2000,
        });
      },
      onError: (err: any) => {
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
    setDialog({ open: false });
  }

  useEffect(() => {
    if (base64File) handleValidate();
  }, [base64File]);

  const message =
    base64File && !errors
      ? "Seems like there are no errors. Please proceed on importing ,also you can download the report to verify."
      : "Please proceed on uploading excel file to validate.";

  return (
    <>
      {!isUploaded ? (
        <div className="w-full h-full bg-white rounded-lg shadow-sm flex flex-col min-h-[600px]">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h6 className="text-lg font-medium">Historic Onboarding</h6>
            <ButtonPrimary onClick={() => downloadTemplate()}>
              Download Template
            </ButtonPrimary>
          </div>

          {!base64File ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8">
              <div className="w-full max-w-2xl space-y-8">
                <div className="text-center space-y-2">
                  <h3 className="text-xl font-medium text-gray-900">
                    Upload File
                  </h3>
                  <p className="text-gray-500">
                    Please ensure your file follows the template format before
                    uploading.
                  </p>
                </div>
                <div className="w-full max-w-2xl mx-auto">
                  <FileDropzone
                    onFileSelect={handleFileSelect}
                    selectedFile={selectedFile}
                    onClear={clearFile}
                    instructionText="Excel files only (.xlsx, .xls, .ods)"
                  />
                  <p className="mt-3 text-xs italic text-gray-500 text-center">
                    Important: Please upload Excel sheets with no empty rows
                    before the header row for accurate data processing.
                  </p>
                </div>
                <Instructions data={onboardingInstructions} />
              </div>
            </div>
          ) : (
            <VStack className="flex-1 justify-between px-6 py-4">
              <VStack className="gap-6">
                {errors && (
                  <HStack className="flex-wrap items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-700 mr-1">
                      Validation errors found:
                    </p>
                    {basicValidationErrors.length > 0 && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                        Basic: {basicValidationErrors.length}
                      </span>
                    )}
                    {response.employeeErrors.length > 0 && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                        Employees: {response.employeeErrors.length}
                      </span>
                    )}
                    {response.grantErrors.length > 0 && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                        Grants: {response.grantErrors.length}
                      </span>
                    )}
                    {response.vestingErrors.length > 0 && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                        Vestings: {response.vestingErrors.length}
                      </span>
                    )}
                    {response.exerciseErrors.length > 0 && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                        Exercises: {response.exerciseErrors.length}
                      </span>
                    )}
                  </HStack>
                )}
                {!errors && isValidating && (
                  <Box className="items-center text-lg font-medium text-center pt-44 text-gray-dark">
                    <div>{<CircularProgress size={100} />}</div>
                    <p>Validating please wait...</p>
                  </Box>
                )}
                {!errors && isImporting && (
                  <Box className="items-center text-lg font-medium text-center pt-24 text-gray-dark">
                    <div>
                      <CircularProgress size={60} />
                    </div>
                    {importPhaseData ? (
                      <VStack className="items-center gap-3 mt-6 w-full max-w-md mx-auto">
                        <p className="text-base font-semibold">
                          {importPhaseData.message}
                        </p>
                        <p className="text-sm text-gray-500">
                          Step {importPhaseData.step} of{" "}
                          {importPhaseData.totalSteps}
                        </p>
                        <LinearProgress
                          className="w-full"
                          variant="determinate"
                          value={
                            (importPhaseData.step /
                              importPhaseData.totalSteps) *
                            100
                          }
                          color="success"
                        />
                      </VStack>
                    ) : (
                      <p className="mt-4">Importing please wait...</p>
                    )}
                  </Box>
                )}
                {basicValidationErrors && basicValidationErrors.length > 0 && (
                  <Box className="w-full py-4 bg-white rounded-lg min-w-min">
                    <HStack
                      className={`items-center justify-between p-4 ${
                        expandedBasic ? "border-b" : ""
                      }`}
                    >
                      <HStack className="items-center gap-2">
                        <p className="text-lg font-medium text-gray-dark">
                          Basic Validation Errors
                        </p>
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                          {basicValidationErrors.length}
                        </span>
                      </HStack>
                      <HStack className="items-center gap-2">
                        <input
                          type="text"
                          placeholder="Search..."
                          value={searchBasic}
                          onChange={(e) => setSearchBasic(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm border border-gray-300 rounded px-2 py-1 w-36 focus:outline-none"
                        />
                        <ButtonPrimary
                          className="p-4"
                          onClick={() => {
                            setExpandedBasic((state) => !state);
                            setGraphTextBasic((state) =>
                              state === "+" ? "-" : "+"
                            );
                          }}
                        >
                          {graphTextBasic}
                        </ButtonPrimary>
                      </HStack>
                    </HStack>
                    {expandedBasic && (
                      <div
                        className="ag-theme-material w-full"
                        style={{ height: 320 }}
                      >
                        <AgGridReact
                          rowData={basicValidationErrors}
                          columnDefs={basicErrorColDefs}
                          defaultColDef={defaultErrorColDef}
                          quickFilterText={searchBasic}
                          domLayout="normal"
                          suppressRowClickSelection
                        />
                      </div>
                    )}
                  </Box>
                )}
                {employeeErrors && employeeErrors.length > 0 && (
                  <Box className="w-full py-4 bg-white rounded-lg min-w-min">
                    <HStack
                      className={`items-center justify-between p-4 ${
                        expandedEmployee ? "border-b" : ""
                      }`}
                    >
                      <HStack className="items-center gap-2">
                        <p className="text-lg font-medium text-gray-dark">
                          Employee Sheet Errors
                        </p>
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                          {employeeErrors.length}
                        </span>
                      </HStack>
                      <HStack className="items-center gap-2">
                        <input
                          type="text"
                          placeholder="Search..."
                          value={searchEmployee}
                          onChange={(e) => setSearchEmployee(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm border border-gray-300 rounded px-2 py-1 w-36 focus:outline-none"
                        />
                        <ButtonPrimary
                          className="p-4"
                          onClick={() => {
                            setExpandedEmployee((state) => !state);
                            setGraphTextEmployee((state) =>
                              state === "+" ? "-" : "+"
                            );
                          }}
                        >
                          {graphTextEmployee}
                        </ButtonPrimary>
                      </HStack>
                    </HStack>
                    {expandedEmployee && (
                      <div
                        className="ag-theme-material w-full"
                        style={{ height: 320 }}
                      >
                        <AgGridReact
                          rowData={employeeErrors}
                          columnDefs={employeeErrorColDefs}
                          defaultColDef={defaultErrorColDef}
                          quickFilterText={searchEmployee}
                          domLayout="normal"
                          suppressRowClickSelection
                        />
                      </div>
                    )}
                  </Box>
                )}
                {grantErrors && grantErrors.length > 0 && (
                  <Box className="w-full py-4 bg-white rounded-lg min-w-min">
                    <HStack
                      className={`items-center justify-between p-4 ${
                        expandedGrant ? "border-b" : ""
                      }`}
                    >
                      <HStack className="items-center gap-2">
                        <p className="text-lg font-medium text-gray-dark">
                          Grant Sheet Errors
                        </p>
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                          {grantErrors.length}
                        </span>
                      </HStack>
                      <HStack className="items-center gap-2">
                        <input
                          type="text"
                          placeholder="Search..."
                          value={searchGrant}
                          onChange={(e) => setSearchGrant(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm border border-gray-300 rounded px-2 py-1 w-36 focus:outline-none"
                        />
                        <ButtonPrimary
                          className="p-4"
                          onClick={() => {
                            setExpandedGrant((state) => !state);
                            setGraphTextGrant((state) =>
                              state === "+" ? "-" : "+"
                            );
                          }}
                        >
                          {graphTextGrant}
                        </ButtonPrimary>
                      </HStack>
                    </HStack>
                    {expandedGrant && (
                      <div
                        className="ag-theme-material w-full"
                        style={{ height: 320 }}
                      >
                        <AgGridReact
                          rowData={grantErrors}
                          columnDefs={grantSheetErrorColDefs}
                          defaultColDef={defaultErrorColDef}
                          quickFilterText={searchGrant}
                          domLayout="normal"
                          suppressRowClickSelection
                        />
                      </div>
                    )}
                  </Box>
                )}
                {exerciseErrors && exerciseErrors.length > 0 && (
                  <Box className="w-full py-4 bg-white rounded-lg min-w-min">
                    <HStack
                      className={`items-center justify-between p-4 ${
                        expandedExercise ? "border-b" : ""
                      }`}
                    >
                      <HStack className="items-center gap-2">
                        <p className="text-lg font-medium text-gray-dark">
                          Exercise Sheet Errors
                        </p>
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                          {exerciseErrors.length}
                        </span>
                      </HStack>
                      <HStack className="items-center gap-2">
                        <input
                          type="text"
                          placeholder="Search..."
                          value={searchExercise}
                          onChange={(e) => setSearchExercise(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm border border-gray-300 rounded px-2 py-1 w-36 focus:outline-none"
                        />
                        <ButtonPrimary
                          className="p-4"
                          onClick={() => {
                            setExpandedExercise((state) => !state);
                            setGraphTextExercise((state) =>
                              state === "+" ? "-" : "+"
                            );
                          }}
                        >
                          {graphTextExercise}
                        </ButtonPrimary>
                      </HStack>
                    </HStack>
                    {expandedExercise && (
                      <div
                        className="ag-theme-material w-full"
                        style={{ height: 320 }}
                      >
                        <AgGridReact
                          rowData={exerciseErrors}
                          columnDefs={grantErrorColDefs}
                          defaultColDef={defaultErrorColDef}
                          quickFilterText={searchExercise}
                          domLayout="normal"
                          suppressRowClickSelection
                        />
                      </div>
                    )}
                  </Box>
                )}
                {vestingErrors && vestingErrors.length > 0 && (
                  <Box className="w-full py-4 bg-white rounded-lg min-w-min">
                    <HStack
                      className={`items-center justify-between p-4 ${
                        expandedVesting ? "border-b" : ""
                      }`}
                    >
                      <HStack className="items-center gap-2">
                        <p className="text-lg font-medium text-gray-dark">
                          Vesting Sheet Errors
                        </p>
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
                          {vestingErrors.length}
                        </span>
                      </HStack>
                      <HStack className="items-center gap-2">
                        <input
                          type="text"
                          placeholder="Search..."
                          value={searchVesting}
                          onChange={(e) => setSearchVesting(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm border border-gray-300 rounded px-2 py-1 w-36 focus:outline-none"
                        />
                        <ButtonPrimary
                          className="p-4"
                          onClick={() => {
                            setExpandedVesting((state) => !state);
                            setGraphTextVesting((state) =>
                              state === "+" ? "-" : "+"
                            );
                          }}
                        >
                          {graphTextVesting}
                        </ButtonPrimary>
                      </HStack>
                    </HStack>
                    {expandedVesting && (
                      <div
                        className="ag-theme-material w-full"
                        style={{ height: 320 }}
                      >
                        <AgGridReact
                          rowData={vestingErrors}
                          columnDefs={grantErrorColDefs}
                          defaultColDef={defaultErrorColDef}
                          quickFilterText={searchVesting}
                          domLayout="normal"
                          suppressRowClickSelection
                        />
                      </div>
                    )}
                  </Box>
                )}
                {!errors && !isImporting && !isValidating && (
                  <Box className="pt-44">
                    <p className="items-center text-lg font-medium text-center text-gray-dark">
                      {message}
                    </p>
                  </Box>
                )}
              </VStack>
              <HStack className="justify-between gap-4 mb-4">
                <ButtonPrimary1
                  type="reset"
                  className="mr-8 text-red-500"
                  onClick={clearFile}
                >
                  Discard
                </ButtonPrimary1>
                <HStack className="items-center gap-2">
                  <ButtonPrimary
                    onClick={() => setReportDialog({ open: true })}
                    disabled={errors || isValidating || isImporting}
                  >
                    Generate Report
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() =>
                      setDialog({
                        open: true,
                        message: "Do you want to import the records?",
                      })
                    }
                    disabled={errors || isValidating || isImporting}
                  >
                    Upload
                  </ButtonPrimary>
                </HStack>
              </HStack>
            </VStack>
          )}
          <Dialog
            open={reportDialog.open}
            onClose={() => setReportDialog({ open: false })}
            maxWidth="lg"
          >
            <div className="w-[400px] min-h-min mx-auto bg-white rounded-lg">
              <OnboardingReportDialog
                onClose={() => setReportDialog({ open: false })}
                file={base64File ?? ""}
              />
            </div>
          </Dialog>
          <Dialog
            open={dialog.open}
            maxWidth="md"
            onClose={() => setDialog({ open: false })}
          >
            <AlertDialog
              onClose={() => setDialog({ open: false })}
              open={dialog.open}
              message={dialog.message}
              onPrimaryAction={handleImport}
              onSecondaryAction={() => setDialog({ open: false })}
            />
          </Dialog>
        </div>
      ) : (
        <VStack className="w-full h-full px-4 bg-white border-2 rounded-md min-h-[400px]">
          <HStack className="py-4 text-lg font-medium text-left bg-white border-b">
            <h6 className="flex-1">Historic Onboarding</h6>
          </HStack>
          <Box className="pt-6">
            <p className="text-lg font-medium text-center justify-items-start text-gray-dark">
              Onboarding is complete. Below is the summary:
            </p>
          </Box>
          <Box className="pt-6">
            <table className="w-full table-space">
              <thead className="text-xs font-medium text-gray-light">
                <tr className="">
                  <td className="py-2 align-middle"></td>
                  <td className="py-2 align-middle">EXISTING NUMBER</td>
                  <td className="py-2 align-middle">ADDED NUMBER</td>
                  <td className="py-2 align-middle">TOTAL NUMBER</td>
                </tr>
              </thead>
              <tbody>
                {summaryDetails?.map((summary) => (
                  <tr key={summary.name} className="border-t border-dashed ">
                    <td className="py-2 align-middle">
                      <HStack className="">
                        <Box>
                          <p className={`text-xs font-medium text-gray-dark `}>
                            {summary.name}
                          </p>
                        </Box>
                      </HStack>
                    </td>
                    <td className="py-2 align-middle">
                      <Box className="">
                        <HStack>
                          <p className={`text-xs font-medium text-gray-dark`}>
                            {summary.existingNumber}
                          </p>
                        </HStack>
                      </Box>
                    </td>
                    <td className="py-2 align-middle">
                      <HStack>
                        <p className={`text-xs font-medium text-gray-dark`}>
                          {summary.addedNumber}
                        </p>
                      </HStack>
                    </td>
                    <td className="py-2 align-middle">
                      <HStack>
                        <p className={`text-xs font-medium text-gray-dark `}>
                          {summary.afterNumber}
                        </p>
                      </HStack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          <HStack className="justify-end gap-4 pt-6 mb-4 bg-white">
            <ButtonPrimary onClick={() => navigate("/options/allPlans")}>
              Continue
            </ButtonPrimary>
          </HStack>
        </VStack>
      )}
    </>
  );
}

export default GrantDetails;
